import { logError } from '@/utils/logger';
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash, MagnifyingGlass, CurrencyDollar, Pencil, FilePdf, Images, FunnelSimple } from '@phosphor-icons/react';
import SearchableSelect from '@/components/SearchableSelect';
import EvidencePhotos from '@/components/EvidencePhotos';

const RECORD_TYPES = [
  { value: 'infraction', label: 'Infraction', color: 'bg-[#EF4444]/20 text-[#EF4444] border-[#EF4444]/30' },
  { value: 'avertissement', label: 'Avertissement', color: 'bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/30' },
  { value: 'conge', label: 'Congé', color: 'bg-[#2563EB]/20 text-[#2563EB] border-[#2563EB]/30' },
  { value: 'absence', label: 'Absence', color: 'bg-[#8B5CF6]/20 text-[#8B5CF6] border-[#8B5CF6]/30' },
  { value: 'felicitations', label: 'Félicitations', color: 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30' },
  { value: 'transfert', label: 'Transfert de site', color: 'bg-[#06B6D4]/20 text-[#06B6D4] border-[#06B6D4]/30' },
  { value: 'autre', label: 'Autre', color: 'bg-[#94A3B8]/20 text-[#94A3B8] border-[#94A3B8]/30' },
];

export const AgentDossier = ({ agent, open, onOpenChange, userRole }) => {
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: 'infraction', description: '', date: new Date().toISOString().split('T')[0], end_date: '', photos: [] });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ description: '', end_date: '', photos: [] });
  const [photosOnly, setPhotosOnly] = useState(false);
  const [view, setView] = useState('list'); // 'list' | 'album'
  const API = process.env.REACT_APP_BACKEND_URL;

  const fetchRecords = useCallback(async () => {
    if (!agent) return;
    try {
      const res = await fetch(`${API}/api/agents/${agent.agent_id}/records`, { credentials: 'include' });
      if (res.ok) setRecords(await res.json());
    } catch (error) { logError('Fetch records:', error); }
  }, [API, agent]);

  useEffect(() => { if (open) { fetchRecords(); setShowForm(false); setSearch(''); setEditingId(null); setPhotosOnly(false); setView('list'); } }, [open, fetchRecords]);

  const startEdit = (rec) => {
    setEditingId(rec.record_id);
    setEditForm({ description: rec.description || '', end_date: rec.end_date || '', photos: rec.photos || [] });
    setShowForm(false);
  };

  const saveEdit = async (recordId) => {
    try {
      const res = await fetch(`${API}/api/agents/records/${recordId}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      });
      if (res.ok) { toast.success('Modifié'); setEditingId(null); fetchRecords(); }
      else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/agents/${agent.agent_id}/records`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success('Enregistré au dossier');
        setForm({ type: 'infraction', description: '', date: new Date().toISOString().split('T')[0], end_date: '', photos: [] });
        setShowForm(false);
        fetchRecords();
      } else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur'); }
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm('Supprimer cet enregistrement ?')) return;
    try {
      const res = await fetch(`${API}/api/agents/records/${recordId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Supprimé'); fetchRecords(); }
      else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur'); }
  };

  if (!agent) return null;
  const typeInfo = (t) => RECORD_TYPES.find(r => r.value === t) || RECORD_TYPES[5];
  const baseFiltered = records.filter(r => !search || r.description?.toLowerCase().includes(search.toLowerCase()) || typeInfo(r.type).label.toLowerCase().includes(search.toLowerCase()));
  const filtered = photosOnly ? baseFiltered.filter(r => (r.photos || []).length > 0) : baseFiltered;
  const allPhotos = records.flatMap(r => (r.photos || []).map(url => ({ url, record: r })));
  const canEdit = ['admin', 'drh', 'supervisor', 'inspector'].includes(userRole);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-3">
            {agent.photo_url ? <img src={agent.photo_url} alt="" className="h-9 w-9 rounded-full object-cover" /> :
              <div className="h-9 w-9 rounded-full bg-[#2563EB] flex items-center justify-center text-white text-sm">{agent.name?.charAt(0)}</div>}
            Dossier — {agent.name}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-3 p-3 bg-[#1E293B]/40 rounded-md border border-[#334155]/50">
          <div className="flex items-center gap-2">
            <CurrencyDollar size={18} className="text-[#10B981]" weight="duotone" />
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#64748B]">Salaire / Rémunération</p>
              <p className="text-sm font-semibold text-[#10B981]" data-testid="dossier-salary">{agent.remuneration ? `${agent.remuneration} $` : 'Non renseigné'}</p>
            </div>
          </div>
          <div className="ml-auto text-right">
            <p className="text-[10px] uppercase tracking-wider text-[#64748B]">Matricule</p>
            <p className="text-sm font-mono text-[#94A3B8]">{agent.matricule || '-'}</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher dans le dossier..." className="bg-[#1E293B] border-[#334155] text-[#F8FAFC] pl-9 h-9" data-testid="dossier-search" />
          </div>
          <Button
            type="button"
            variant={photosOnly ? 'default' : 'outline'}
            onClick={() => setPhotosOnly(!photosOnly)}
            className={photosOnly ? 'bg-[#F59E0B] hover:bg-[#D97706] text-white h-9' : 'border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B] h-9'}
            title="Filtrer records avec photos"
            data-testid="filter-photos-only"
          >
            <FunnelSimple size={16} className="mr-1" /> Avec photos
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => window.open(`${API}/api/agents/${agent.agent_id}/dossier/pdf`, '_blank')}
            className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B] h-9"
            title="Télécharger le dossier complet en PDF"
            data-testid="dossier-pdf-button"
          >
            <FilePdf size={16} className="mr-1" /> PDF
          </Button>
          {canEdit && (
            <Button onClick={() => { setShowForm(!showForm); setEditingId(null); }} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-9" data-testid="dossier-add-button">
              <Plus size={16} className="mr-1.5" /> Ajouter
            </Button>
          )}
        </div>

        {/* Onglets Liste / Album */}
        <div className="flex gap-1 border-b border-[#1E293B]">
          <button
            type="button"
            onClick={() => setView('list')}
            className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${view === 'list' ? 'border-[#2563EB] text-[#F8FAFC]' : 'border-transparent text-[#94A3B8] hover:text-[#F8FAFC]'}`}
            data-testid="tab-list"
          >
            Liste ({filtered.length})
          </button>
          <button
            type="button"
            onClick={() => setView('album')}
            className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors flex items-center gap-1 ${view === 'album' ? 'border-[#F59E0B] text-[#F8FAFC]' : 'border-transparent text-[#94A3B8] hover:text-[#F8FAFC]'}`}
            data-testid="tab-album"
          >
            <Images size={14} weight="duotone" /> Album ({allPhotos.length})
          </button>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="space-y-3 p-4 bg-[#1E293B]/30 rounded-md border border-[#334155]">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-[#F8FAFC] text-xs">Type</Label>
                <div className="mt-1">
                  <SearchableSelect value={form.type} onValueChange={v => setForm({ ...form, type: v })} options={RECORD_TYPES.map(r => ({ value: r.value, label: r.label }))} testId="dossier-type-select" />
                </div>
              </div>
              <div>
                <Label className="text-[#F8FAFC] text-xs">Date {form.type === 'conge' ? 'de début' : ''}</Label>
                <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="dossier-date" />
              </div>
              {(form.type === 'conge' || form.type === 'absence') && (
                <div>
                  <Label className="text-[#F8FAFC] text-xs">Date de fin</Label>
                  <Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="dossier-end-date" />
                </div>
              )}
            </div>
            <div>
              <Label className="text-[#F8FAFC] text-xs">Description *</Label>
              <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required rows={2} placeholder="Décrivez l'événement..." className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="dossier-description" />
            </div>
            {['infraction', 'avertissement', 'felicitations'].includes(form.type) && (
              <div>
                <Label className="text-[#F8FAFC] text-xs">Preuves visuelles (photos terrain)</Label>
                <div className="mt-1">
                  <EvidencePhotos photos={form.photos} onChange={(p) => setForm({ ...form, photos: p })} max={6} />
                </div>
              </div>
            )}
            <div className="flex justify-end">
              <Button type="submit" className="bg-[#10B981] hover:bg-[#059669] text-white h-9" data-testid="dossier-save-button">Enregistrer au dossier</Button>
            </div>
          </form>
        )}

        {view === 'list' && (
        <div className="space-y-2">
          {filtered.length === 0 ? (
            <p className="text-center text-[#64748B] text-sm py-6">Aucun enregistrement {photosOnly ? 'avec photo' : 'dans le dossier'}</p>
          ) : filtered.map(rec => (
            <div key={rec.record_id} className="flex items-start justify-between gap-3 p-3 bg-[#1E293B]/30 rounded-md border border-[#334155]/50" data-testid={`record-${rec.record_id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className={`${typeInfo(rec.type).color} border text-xs`}>{typeInfo(rec.type).label}</Badge>
                  <span className="text-xs text-[#64748B]">
                    {new Date(rec.date).toLocaleDateString('fr-FR')}
                    {rec.end_date && ` → ${new Date(rec.end_date).toLocaleDateString('fr-FR')}`}
                  </span>
                </div>
                {editingId === rec.record_id ? (
                  <div className="mt-2 space-y-2">
                    <Textarea value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} rows={2} className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid={`edit-desc-${rec.record_id}`} />
                    {(rec.type === 'conge' || rec.type === 'absence') && (
                      <Input type="date" value={editForm.end_date || ''} onChange={e => setEditForm({ ...editForm, end_date: e.target.value })} className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" />
                    )}
                    <EvidencePhotos photos={editForm.photos} onChange={(p) => setEditForm({ ...editForm, photos: p })} max={6} />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => saveEdit(rec.record_id)} className="bg-[#10B981] hover:bg-[#059669] text-white h-8" data-testid={`save-edit-${rec.record_id}`}>Enregistrer</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B] h-8">Annuler</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-[#F8FAFC] mt-1">{rec.description}</p>
                    {rec.photos && rec.photos.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {rec.photos.map((url, i) => (
                          <a key={i} href={url} target="_blank" rel="noopener noreferrer" title="Voir en grand">
                            <img src={url} alt={`preuve ${i + 1}`} className="h-14 w-14 object-cover rounded border border-[#334155] hover:border-[#2563EB] transition-colors" />
                          </a>
                        ))}
                      </div>
                    )}
                    {rec.created_by_name && <p className="text-[10px] text-[#64748B] mt-1">Par {rec.created_by_name}</p>}
                  </>
                )}
              </div>
              {editingId !== rec.record_id && (
                <div className="flex-shrink-0 flex gap-1">
                  {canEdit && (
                    <Button size="sm" variant="ghost" onClick={() => startEdit(rec)} className="text-[#2563EB] hover:text-white hover:bg-[#2563EB] h-7 w-7 p-0" data-testid={`edit-record-${rec.record_id}`}><Pencil size={14} /></Button>
                  )}
                  {userRole === 'admin' && (
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(rec.record_id)} className="text-[#EF4444] hover:text-white hover:bg-[#EF4444] h-7 w-7 p-0"><Trash size={14} /></Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        )}

        {view === 'album' && (
          <div>
            {allPhotos.length === 0 ? (
              <p className="text-center text-[#64748B] text-sm py-8">Aucune photo dans le dossier de cet agent.</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2" data-testid="album-grid">
                {allPhotos.map(({ url, record }, idx) => (
                  <a key={idx} href={url} target="_blank" rel="noopener noreferrer" className="relative group aspect-square rounded overflow-hidden border border-[#334155] hover:border-[#F59E0B] transition-colors">
                    <img src={url} alt={`preuve ${idx + 1}`} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-1">
                      <Badge className={`${typeInfo(record.type).color} border text-[9px] w-fit`}>{typeInfo(record.type).label}</Badge>
                      <p className="text-[9px] text-white/80 mt-0.5">{new Date(record.date).toLocaleDateString('fr-FR')}</p>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AgentDossier;
